import classNames from 'classnames'
import dateFormat from 'dateformat'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'

import { Portal } from '@components/Portal'
import { StatusItem } from '@components/StatusItem'

import { Button } from '@UI/Button'
import { ActionError } from '@UI/ConstructElements'
import { Loader } from '@UI/Loader'

import { useAnimate } from '@hooks/useAnimate'
import { useAppSelector } from '@hooks/useAppSelector'
import { useCall } from '@hooks/useCall'

import { useRecordCallMutation, useSearchByPhoneQuery } from '@/store/shop/orders/orders.api'
import { CallStatus } from '@/types'
import { ReactComponent as ArrowIcon } from '@assets/icons/arrow.svg'
import { ReactComponent as CallIcon } from '@assets/icons/call.svg'
import { ReactComponent as EndCallIcon } from '@assets/icons/end-call.svg'
import { ReactComponent as PauseIcon } from '@assets/icons/pause.svg'
import { ReactComponent as PlayIcon } from '@assets/icons/play.svg'

import s from './CallModal.module.scss'
import type { CallModalProps, EndCallButtonProps } from './CallModal.props'

const statuses: Record<CallStatus, string> = {
  connected: 'call.status.connected',
  ended: 'call.status.ended',
  failed: 'call.status.failed',
  incoming: 'call.status.incoming',
  outgoing: 'call.status.outgoing',
  paused: 'call.status.paused',
  unpaused: 'call.status.unpaused',
  '': '',
}

export function CallModal({}: CallModalProps) {
  const { t } = useTranslation()

  const bodyStyles = useRef(document.body.style)

  const { orderId } = useAppSelector((state) => state.orders)

  const { status, isIncoming, target, closeModal, makeCall, answerCall, endCall, holdCall, unholdCall } = useCall()

  const phone = target?.match(/(?:sip:\+)?(\d+)/)?.[1]

  const { data, isLoading, error } = useSearchByPhoneQuery({ phone: target }, { skip: !target })
  const [recordCall] = useRecordCallMutation()
  const [isAnimate, closeHandler] = useAnimate(closeModal)
  const [minimized, setMinimized] = useState(false)

  const endCallCallback = () => {
    if (!phone) return

    recordCall({
      phone,
      orderId,
    })
      .unwrap()
      .catch(console.error)
  }

  const RenderButton = () => {
    switch (status) {
      case 'connected':
      case 'unpaused':
        return (
          <Button
            onClick={holdCall}
            className={s.holdBtn}
          >
            <PauseIcon />
            {t('call.hold')}
          </Button>
        )
      case 'paused':
        return (
          <Button
            onClick={unholdCall}
            className={s.unholdBtn}
          >
            <PlayIcon />
            {t('call.unhold')}
          </Button>
        )
      case 'incoming':
        return (
          <Button
            onClick={answerCall}
            className={s.callBtn}
          >
            <CallIcon />
            {t('call.answer')}
          </Button>
        )
      case '':
      case 'outgoing':
      case 'ended':
      case 'failed':
        return (
          <Button
            onClick={makeCall}
            disabled={status === 'outgoing'}
            className={s.callBtn}
          >
            <CallIcon />
            {t('call.makeCall')}
          </Button>
        )
      default:
        return null
    }
  }

  useEffect(() => {
    const lockWindowScroll = () => {
      document.body.style.overflowY = 'hidden'
    }

    const unlockWindowScroll = () => {
      if (bodyStyles.current.overflowY !== 'hidden') {
        document.body.style.overflowY = 'auto'
      }
    }

    lockWindowScroll()

    return () => {
      unlockWindowScroll()
    }
  }, [])

  useEffect(() => {
    if (status === 'ended' || status === 'failed') {
      setMinimized(false)
    }
    if (status === 'ended') {
      endCallCallback()
    }
  }, [status])

  return (
    <Portal>
      <div
        className={classNames(s.overlay, {
          [s.animate]: isAnimate,
          [s.minimized]: minimized,
        })}
        onClick={() => ['ended', 'failed', ''].includes(status) && closeHandler()}
      />
      <div
        className={classNames(s.modal, {
          [s.animate]: isAnimate,
          [s.minimized]: minimized,
        })}
      >
        {!minimized && (
          <div className={s.modalHeader}>
            <h2 className={s.modalTitle}>
              {t('call.title')} {isIncoming ? t('call.from') : t('call.to')} {phone}
            </h2>

            {['connected', 'unpaused', 'paused'].includes(status) && (
              <div
                className={s.minimized}
                onClick={() => setMinimized(true)}
              >
                {t('call.minimize')}
              </div>
            )}
          </div>
        )}

        {isLoading && <Loader />}
        {error && 'data' in error && <ActionError>{error?.data?.message}</ActionError>}
        {!isLoading && !error && !data?.phone && <ActionError>Invalid phone number</ActionError>}
        {!isLoading && !error && data?.phone && (
          <>
            {!minimized && (
              <>
                {data.client?.name && (
                  <>
                    <h4 className={s.title}>{t('call.client')}</h4>
                    <div className={s.userWrapper}>
                      {data.client?.avatar && (
                        <img
                          src={data.client.avatar}
                          alt="avatar"
                          className={s.avatar}
                        />
                      )}
                      <div className={s.name}>{data.client.name}</div>
                    </div>
                    <hr className={s.hr} />
                  </>
                )}
                {data.responsible?.name && (
                  <>
                    <h4 className={s.title}>{t('call.responsible')}</h4>
                    <div className={s.userWrapper}>
                      {data.responsible?.avatar && (
                        <img
                          src={data.responsible.avatar}
                          alt="avatar"
                          className={s.avatar}
                        />
                      )}
                      <div className={s.name}>{data.responsible.name}</div>
                    </div>
                    <hr className={s.hr} />
                  </>
                )}
                {!!data.orders?.length && (
                  <>
                    <h4 className={s.title}>{t('call.orders')}</h4>
                    <SimpleBar style={{ maxHeight: '200px', height: '100%' }}>
                      <ul className={s.list}>
                        {data.orders.map((order) => (
                          <li
                            key={order.id}
                            className={s.listItem}
                          >
                            <div className={s.listItemContent}>
                              {t('call.order')} №{order.id} {t('call.from')} {dateFormat(order.createdAt, 'dd.mm.yyyy - HH:MM')}
                              <div className={s.price}>{order.total}</div>
                            </div>
                            <StatusItem
                              title={order.orderStatus.name}
                              style={{ borderBottom: `1px solid ${order.orderStatus.backgroundColor}` }}
                            />
                          </li>
                        ))}
                      </ul>
                    </SimpleBar>
                    <hr className={s.hr} />
                  </>
                )}
              </>
            )}

            {!minimized && <div className={s.status}>{t(statuses[status])}</div>}

            <div className={s.btnsWrapper}>
              {!minimized && <RenderButton />}

              {status && !['ended', 'failed'].includes(status) && (
                <EndCallButton
                  status={status}
                  onClick={endCall}
                >
                  {status === 'incoming' ? t('call.cancel') : t('call.end')}
                </EndCallButton>
              )}

              {minimized && (
                <div
                  onClick={() => setMinimized(false)}
                  className={s.unminimize}
                >
                  <ArrowIcon />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Portal>
  )
}

function EndCallButton({ children, status, onClick }: EndCallButtonProps) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const [callDuration, setCallDuration] = useState(0)

  const clearTimer = () => {
    setCallDuration(0)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  useEffect(() => {
    if (status === 'connected') {
      intervalRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1)
      }, 1000)
    } else if (status === 'ended' || status === 'failed') {
      clearTimer()
    }
  }, [status])

  useEffect(
    () => () => {
      clearTimer()
    },
    [],
  )

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  return (
    <Button
      className={s.endBtn}
      onClick={onClick}
    >
      <EndCallIcon />

      {children}

      {!!callDuration && <span className={s.timer}>{formatDuration(callDuration)}</span>}
    </Button>
  )
}
